import { useEffect, useState, useRef } from "react";
import { RefreshCw, Loader2, Upload } from "lucide-react";
import { Button } from "@/components/ui/button";
import api from "@/lib/api";
import { useToast } from "@/hooks/use-toast";

export default function MetaSyncPanel() {
  const [lastSync, setLastSync] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);
  const { toast } = useToast();

  const fetchLog = () => api.get("/admin/performance/sync-log", { params: { limit: 1 } }).then(({ data }) => setLastSync(data.items[0] || null));

  useEffect(() => {
    fetchLog();
  }, []);

  const handleSync = async () => {
    setSyncing(true);
    try {
      const { data } = await api.post("/admin/performance/meta-sync");
      toast({
        title: data.status === "success" ? `Synced ${data.rows_upserted} rows` : "Sync failed",
        description: data.error_message,
        variant: data.status === "success" ? "default" : "destructive",
      });
      setLastSync(data);
    } catch {
      toast({ variant: "destructive", title: "Sync failed" });
    } finally {
      setSyncing(false);
    }
  };

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    const formData = new FormData();
    formData.append("file", file);
    try {
      const { data } = await api.post("/admin/performance/upload-csv", formData, { headers: { "Content-Type": "multipart/form-data" } });
      toast({ title: `Uploaded: ${data.rows_upserted} rows (${data.rows_skipped} skipped)` });
    } catch (err) {
      toast({ variant: "destructive", title: "Upload failed", description: err.response?.data?.detail });
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  return (
    <div className="flex items-center gap-2 flex-wrap" data-testid="meta-sync-panel">
      {lastSync && (
        <span className="text-xs text-amtm-textMuted" data-testid="meta-sync-last-status">
          Last sync: {lastSync.status} ({new Date(lastSync.finished_at || lastSync.started_at).toLocaleString("en-IN")})
        </span>
      )}
      <Button type="button" variant="outline" onClick={handleSync} disabled={syncing} data-testid="meta-sync-now-button">
        {syncing ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-2" />} Sync Now
      </Button>
      <Button type="button" variant="outline" onClick={() => fileInputRef.current?.click()} disabled={uploading} data-testid="meta-csv-upload-button">
        {uploading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Upload className="h-4 w-4 mr-2" />} Upload Meta CSV
      </Button>
      <input ref={fileInputRef} type="file" accept=".csv" className="hidden" onChange={handleUpload} data-testid="meta-csv-upload-input" />
    </div>
  );
}
